/**
 * Emitter presets + the per-frame request queue. Recipes call `req(...)` to ask for a burst; at
 * flush time the budget arbitrates all requests of the frame (highest priority first, per screen
 * region and against free pool slots) and the granted particles are spawned into the pools.
 * Requests are pooled objects; nothing is allocated per frame.
 */
import * as THREE from 'three';
import { type BudgetRequest, arbitrate, regionOf } from './budget';
import { PF_BOUNCE, PF_FLOOR_FADE, PF_GROUND_CLEAR, type ParticleInit, type ParticlePool, blankParticle } from './pool';
import { FRAME } from './textures';
import type { RGB } from './table';
import { groundY } from '../ground';

export const P = {
  sparks: 0,
  embers: 1,
  flash: 2,
  glow: 3,
  ring: 4,
  smoke: 5,
  dust: 6,
  debris: 7,
  motes: 8,
  shards: 9,
  converge: 10,
  flames: 11,
  wisps: 12,
  stars: 13,
  crescent: 14,
  boltlets: 15,
  streaks: 16,
  dustRing: 17,
  drips: 18,
} as const;
export type Preset = (typeof P)[keyof typeof P];

/** Presets drawn with normal alpha blending (smoke, dust, debris); everything else is additive. */
const ALPHA_PRESETS = new Set<number>([P.smoke, P.dust, P.debris, P.dustRing]);

export interface EmitReq extends BudgetRequest {
  preset: Preset;
  x: number;
  y: number;
  z: number;
  /** Direction (sparks, streaks) or nothing. */
  dx: number;
  dy: number;
  dz: number;
  r: number;
  g: number;
  b: number;
  r2: number;
  g2: number;
  b2: number;
  /** Particle size scale (yards). */
  size: number;
  /** Speed scale (yd/s). */
  speed: number;
  /** Spawn radius (yards). */
  radius: number;
  /** Opacity multiplier. */
  alpha: number;
  /** Lifetime multiplier. */
  life: number;
  /** Rotation for oriented presets (crescent). */
  rot: number;
  /** Friendly: fade near the floor so danger zones stay readable. */
  clearFloor: boolean;
}

const MAX_REQ = 384;
const REGION_COLS = 4;
const REGION_ROWS = 3;
export const REGIONS = REGION_COLS * REGION_ROWS;

function blankReq(): EmitReq {
  return {
    preset: P.sparks, priority: 1, count: 0, region: 0, cost: 1, x: 0, y: 0, z: 0, dx: 0, dy: 0, dz: 0,
    r: 1, g: 1, b: 1, r2: 1, g2: 1, b2: 1, size: 1, speed: 1, radius: 0, alpha: 1, life: 1, rot: 0, clearFloor: false,
  };
}

const rnd = Math.random;
const rr = (a: number, b: number): number => a + (b - a) * rnd();

export class Emitters {
  private readonly reqs: EmitReq[] = [];
  private n = 0;
  private readonly sink = blankReq();
  private readonly granted = new Int32Array(MAX_REQ);
  private readonly order = new Int32Array(MAX_REQ);
  private readonly loadScratch = new Float32Array(REGIONS);
  private readonly loadSum = new Float32Array(REGIONS);
  private readonly p: ParticleInit = blankParticle();
  private readonly v = new THREE.Vector3();
  /** Particle count multiplier (effects intensity). */
  countScale = 1;
  /** Per-region overdraw cap (sum of particle cost). */
  regionCap = 180;
  /** Stats (debug). */
  dropped = 0;

  constructor(private readonly add: ParticlePool, private readonly alpha: ParticlePool) {
    for (let i = 0; i < MAX_REQ; i++) this.reqs.push(blankReq());
  }

  /** Queue a burst. Returns a request to fill in (or a throwaway sink when the queue is full). */
  req(preset: Preset, priority: number, count: number, x: number, y: number, z: number): EmitReq {
    const n = Math.round(count * this.countScale);
    if (n <= 0 || this.n >= MAX_REQ) return this.reset(this.sink, preset, priority, 0, x, y, z);
    return this.reset(this.reqs[this.n++]!, preset, priority, n, x, y, z);
  }

  private reset(q: EmitReq, preset: Preset, priority: number, count: number, x: number, y: number, z: number): EmitReq {
    q.preset = preset;
    q.priority = priority;
    q.count = count;
    q.x = x;
    q.y = y;
    q.z = z;
    q.dx = q.dy = q.dz = 0;
    q.r = q.g = q.b = 1;
    q.r2 = q.g2 = q.b2 = 1;
    q.size = 1;
    q.speed = 1;
    q.radius = 0;
    q.alpha = 1;
    q.life = 1;
    q.rot = 0;
    q.clearFloor = false;
    return q;
  }

  /** Arbitrate this frame's requests against the budget and spawn the granted particles. */
  flush(camera: THREE.Camera): void {
    const n = this.n;
    this.n = 0;
    if (n === 0) return;
    for (let i = 0; i < n; i++) {
      const q = this.reqs[i]!;
      this.v.set(q.x, q.y, q.z).project(camera);
      q.region = regionOf(this.v.x, this.v.y, REGION_COLS, REGION_ROWS);
      q.cost = presetCost(q);
    }
    for (let r = 0; r < REGIONS; r++) this.loadSum[r] = this.add.regionLoad[r]! + this.alpha.regionLoad[r]! * 0.5;
    const free = this.add.freeCount + this.alpha.freeCount;
    arbitrate(this.reqs, n, this.loadSum, { regionCap: this.regionCap, globalFree: free }, this.granted, this.order, this.loadScratch);
    for (let i = 0; i < n; i++) {
      const q = this.reqs[i]!;
      const g = this.granted[i]!;
      this.dropped += q.count - g;
      if (g > 0) this.spawn(q, g);
    }
  }

  clear(): void {
    this.n = 0;
  }

  private spawn(q: EmitReq, count: number): void {
    const pool = ALPHA_PRESETS.has(q.preset) ? this.alpha : this.add;
    const p = this.p;
    for (let i = 0; i < count; i++) {
      // Defaults; presets override.
      p.x = q.x;
      p.y = q.y;
      p.z = q.z;
      p.vx = p.vy = p.vz = 0;
      p.rot = rnd() * Math.PI * 2;
      p.rotVel = 0;
      p.gravity = 0;
      p.drag = 0;
      p.stretch = 0;
      p.flags = PF_FLOOR_FADE | (q.clearFloor ? PF_GROUND_CLEAR : 0);
      p.priority = q.priority;
      p.region = q.region;
      p.cost = q.cost;
      p.r0 = q.r2;
      p.g0 = q.g2;
      p.b0 = q.b2;
      p.r1 = q.r;
      p.g1 = q.g;
      p.b1 = q.b;
      p.a0 = 0.9 * q.alpha;
      p.a1 = 0;
      p.frame = FRAME.glow;
      const s = q.size;
      const sp = q.speed;
      switch (q.preset) {
        case P.sparks: {
          dirJitter(q, 0.75, this.v);
          const v = sp * rr(5, 11);
          p.vx = this.v.x * v;
          p.vy = this.v.y * v + rr(0.5, 2.5);
          p.vz = this.v.z * v;
          p.gravity = 11;
          p.drag = 3.2;
          p.stretch = 0.09;
          p.life = rr(0.16, 0.38);
          p.size0 = p.size1 = 0.095 * s * rr(0.7, 1.3);
          p.frame = FRAME.core;
          p.a0 = 1 * q.alpha;
          break;
        }
        case P.embers: {
          const a = rnd() * Math.PI * 2;
          const v = sp * rr(1, 3.5);
          p.x += Math.cos(a) * q.radius * rnd();
          p.z += Math.sin(a) * q.radius * rnd();
          p.vx = Math.cos(a) * v;
          p.vz = Math.sin(a) * v;
          p.vy = sp * rr(1.5, 4.5);
          p.gravity = 5.5;
          p.drag = 1.4;
          p.stretch = 0.05;
          p.life = rr(0.45, 1.0) * q.life;
          p.size0 = 0.09 * s;
          p.size1 = 0.04 * s;
          p.frame = FRAME.ember;
          p.flags |= PF_BOUNCE;
          break;
        }
        case P.flash: {
          p.life = 0.17 * q.life;
          p.size0 = 0.5 * s;
          p.size1 = 1.5 * s;
          p.frame = FRAME.core;
          p.r0 = q.r2;
          p.g0 = q.g2;
          p.b0 = q.b2;
          p.a0 = 0.75 * q.alpha;
          break;
        }
        case P.glow: {
          p.life = 0.3 * q.life;
          p.size0 = 0.8 * s;
          p.size1 = 1.1 * s;
          p.a0 = 0.5 * q.alpha;
          p.r0 = q.r;
          p.g0 = q.g;
          p.b0 = q.b;
          break;
        }
        case P.ring: {
          p.life = 0.32 * q.life;
          p.size0 = 0.3 * s;
          p.size1 = 2.2 * s;
          p.frame = FRAME.ring;
          p.a0 = 0.85 * q.alpha;
          p.rotVel = 0;
          break;
        }
        case P.smoke: {
          const a = rnd() * Math.PI * 2;
          p.x += Math.cos(a) * q.radius * rnd();
          p.z += Math.sin(a) * q.radius * rnd();
          p.vx = Math.cos(a) * sp * rr(0.3, 1.2);
          p.vz = Math.sin(a) * sp * rr(0.3, 1.2);
          p.vy = sp * rr(0.5, 1.6);
          p.drag = 1.8;
          p.life = rr(0.8, 1.4) * q.life;
          p.size0 = 0.45 * s;
          p.size1 = 1.3 * s;
          p.rotVel = rr(-0.8, 0.8);
          p.frame = FRAME.smoke;
          p.r0 = p.r1 = q.r;
          p.g0 = p.g1 = q.g;
          p.b0 = p.b1 = q.b;
          p.a0 = 0.55 * q.alpha;
          break;
        }
        case P.dust: {
          const a = rnd() * Math.PI * 2;
          const d = q.radius * Math.sqrt(rnd());
          p.x += Math.cos(a) * d;
          p.z += Math.sin(a) * d;
          p.y = groundY(p.x, -p.z) + 0.25 + rnd() * 0.3;
          p.vx = Math.cos(a) * sp * rr(1, 3.5);
          p.vz = Math.sin(a) * sp * rr(1, 3.5);
          p.vy = rr(0.3, 1.0);
          p.drag = 2.6;
          p.life = rr(0.7, 1.2) * q.life;
          p.size0 = 0.5 * s;
          p.size1 = 1.5 * s;
          p.rotVel = rr(-0.5, 0.5);
          p.frame = FRAME.dust;
          p.r0 = p.r1 = q.r;
          p.g0 = p.g1 = q.g;
          p.b0 = p.b1 = q.b;
          p.a0 = 0.6 * q.alpha;
          p.flags = 0;
          break;
        }
        case P.dustRing: {
          const a = rnd() * Math.PI * 2;
          p.x += Math.cos(a) * q.radius;
          p.z += Math.sin(a) * q.radius;
          p.y = groundY(p.x, -p.z) + 0.3;
          p.vx = Math.cos(a) * sp * rr(2, 4.5);
          p.vz = Math.sin(a) * sp * rr(2, 4.5);
          p.vy = rr(0.2, 0.8);
          p.drag = 3;
          p.life = rr(0.6, 1.0) * q.life;
          p.size0 = 0.5 * s;
          p.size1 = 1.4 * s;
          p.frame = FRAME.dust;
          p.r0 = p.r1 = q.r;
          p.g0 = p.g1 = q.g;
          p.b0 = p.b1 = q.b;
          p.a0 = 0.55 * q.alpha;
          p.flags = 0;
          break;
        }
        case P.debris: {
          const a = rnd() * Math.PI * 2;
          const d = q.radius * Math.sqrt(rnd());
          p.x += Math.cos(a) * d;
          p.z += Math.sin(a) * d;
          const out = sp * rr(0.8, 3);
          p.vx = Math.cos(a) * out;
          p.vz = Math.sin(a) * out;
          p.vy = sp * rr(4, 8);
          p.gravity = 20;
          p.drag = 0.3;
          p.life = rr(0.8, 1.3) * q.life;
          p.size0 = p.size1 = 0.18 * s * rr(0.6, 1.4);
          p.rotVel = rr(-9, 9);
          p.frame = FRAME.debris;
          p.r0 = p.r1 = q.r;
          p.g0 = p.g1 = q.g;
          p.b0 = p.b1 = q.b;
          p.a0 = 1 * q.alpha;
          p.a1 = 0.85 * q.alpha;
          p.flags = PF_BOUNCE;
          break;
        }
        case P.motes: {
          const a = rnd() * Math.PI * 2;
          const d = q.radius * rr(0.4, 1);
          p.x += Math.cos(a) * d;
          p.z += Math.sin(a) * d;
          p.y += rr(-0.2, 0.4);
          const tang = sp * rr(0.6, 1.4);
          p.vx = -Math.sin(a) * tang;
          p.vz = Math.cos(a) * tang;
          p.vy = sp * rr(1.4, 2.8);
          p.gravity = -0.6;
          p.drag = 0.9;
          p.life = rr(0.7, 1.2) * q.life;
          p.size0 = 0.2 * s;
          p.size1 = 0.06 * s;
          p.frame = rnd() < 0.3 ? FRAME.cross : FRAME.glow;
          break;
        }
        case P.shards: {
          dirJitter(q, 1.0, this.v);
          const v = sp * rr(6, 12);
          p.vx = this.v.x * v;
          p.vy = this.v.y * v + rr(1, 4);
          p.vz = this.v.z * v;
          p.gravity = 12;
          p.drag = 2.2;
          p.stretch = 0.05;
          p.life = rr(0.28, 0.5);
          p.size0 = 0.22 * s;
          p.size1 = 0.12 * s;
          p.frame = FRAME.shard;
          p.a0 = 1 * q.alpha;
          break;
        }
        case P.converge: {
          randomUnit(this.v);
          const R = q.radius * rr(0.6, 1);
          const life = rr(0.22, 0.38) * q.life;
          p.x += this.v.x * R;
          p.y += this.v.y * R * 0.7;
          p.z += this.v.z * R;
          p.vx = (-this.v.x * R) / life;
          p.vy = (-this.v.y * R * 0.7) / life;
          p.vz = (-this.v.z * R) / life;
          p.stretch = 0.04;
          p.life = life;
          p.size0 = 0.06 * s;
          p.size1 = 0.13 * s;
          p.frame = FRAME.ember;
          p.a0 = 0.15 * q.alpha;
          p.a1 = 0.9 * q.alpha;
          p.flags = 0;
          break;
        }
        case P.flames: {
          const a = rnd() * Math.PI * 2;
          const d = q.radius * Math.sqrt(rnd());
          p.x += Math.cos(a) * d;
          p.z += Math.sin(a) * d;
          p.vx = rr(-0.4, 0.4);
          p.vz = rr(-0.4, 0.4);
          p.vy = sp * rr(1.8, 3.4);
          p.gravity = -2;
          p.drag = 1.2;
          p.life = rr(0.28, 0.5) * q.life;
          p.size0 = 0.42 * s;
          p.size1 = 0.12 * s;
          p.rot = rr(-0.3, 0.3);
          p.frame = FRAME.flame;
          p.a0 = 0.85 * q.alpha;
          break;
        }
        case P.wisps: {
          const a = rnd() * Math.PI * 2;
          const d = q.radius * Math.sqrt(rnd());
          p.x += Math.cos(a) * d;
          p.z += Math.sin(a) * d;
          p.vy = sp * rr(0.9, 1.8);
          p.vx = rr(-0.3, 0.3);
          p.vz = rr(-0.3, 0.3);
          p.drag = 0.4;
          p.life = rr(0.9, 1.5) * q.life;
          p.size0 = 0.45 * s;
          p.size1 = 0.9 * s;
          p.rot = 0;
          p.frame = FRAME.wisp;
          p.a0 = 0.45 * q.alpha;
          break;
        }
        case P.stars: {
          randomUnit(this.v);
          const d = q.radius * Math.cbrt(rnd());
          p.x += this.v.x * d;
          p.y += this.v.y * d * 0.6;
          p.z += this.v.z * d;
          p.vy = sp * rr(0.3, 1.2);
          p.life = rr(0.35, 0.7) * q.life;
          p.size0 = 0.3 * s;
          p.size1 = 0.02;
          p.rotVel = rr(-3, 3);
          p.frame = rnd() < 0.5 ? FRAME.star : FRAME.cross;
          p.a0 = 0.9 * q.alpha;
          break;
        }
        case P.crescent: {
          p.life = rr(0.13, 0.19) * q.life;
          p.size0 = 1.0 * s;
          p.size1 = 1.5 * s;
          p.rot = q.rot + rr(-0.35, 0.35);
          p.rotVel = rr(-2, 2);
          p.frame = FRAME.crescent;
          p.r0 = q.r2;
          p.g0 = q.g2;
          p.b0 = q.b2;
          p.a0 = 0.85 * q.alpha;
          p.flags = 0;
          break;
        }
        case P.boltlets: {
          randomUnit(this.v);
          const d = q.radius * rr(0.3, 1);
          p.x += this.v.x * d;
          p.y += this.v.y * d * 0.8;
          p.z += this.v.z * d;
          p.life = rr(0.05, 0.1);
          p.size0 = p.size1 = 0.45 * s * rr(0.6, 1.2);
          p.frame = FRAME.bolt;
          p.a0 = p.a1 = 0.9 * q.alpha;
          p.flags = 0;
          break;
        }
        case P.streaks: {
          dirJitter(q, 0.15, this.v);
          const v = sp * rr(10, 18);
          p.x += rr(-1, 1) * q.radius;
          p.y += rr(-0.5, 0.5) * q.radius;
          p.z += rr(-1, 1) * q.radius;
          p.vx = this.v.x * v;
          p.vy = this.v.y * v;
          p.vz = this.v.z * v;
          p.drag = 4;
          p.stretch = 0.12;
          p.life = rr(0.12, 0.25) * q.life;
          p.size0 = p.size1 = 0.06 * s;
          p.frame = FRAME.core;
          p.a0 = 0.7 * q.alpha;
          break;
        }
        case P.drips: {
          const a = rnd() * Math.PI * 2;
          const d = q.radius * rnd();
          p.x += Math.cos(a) * d;
          p.z += Math.sin(a) * d;
          p.vx = Math.cos(a) * sp * rr(0.3, 1.5);
          p.vz = Math.sin(a) * sp * rr(0.3, 1.5);
          p.vy = sp * rr(0.5, 2.5);
          p.gravity = 9;
          p.stretch = 0.06;
          p.life = rr(0.35, 0.65) * q.life;
          p.size0 = 0.09 * s;
          p.size1 = 0.06 * s;
          p.frame = FRAME.ember;
          break;
        }
      }
      if (pool.spawn(p) < 0) break;
    }
  }
}

/** Approximate overdraw cost of one particle of a request. */
function presetCost(q: EmitReq): number {
  let base: number;
  switch (q.preset) {
    case P.flash:
      base = 3;
      break;
    case P.ring:
    case P.crescent:
      base = 3.5;
      break;
    case P.smoke:
    case P.dust:
    case P.dustRing:
      base = 1.6;
      break;
    case P.wisps:
    case P.glow:
    case P.flames:
      base = 0.8;
      break;
    default:
      base = 0.15;
  }
  return Math.max(0.1, base * q.size * q.size);
}

function randomUnit(out: THREE.Vector3): THREE.Vector3 {
  const u = rnd() * 2 - 1;
  const t = rnd() * Math.PI * 2;
  const s = Math.sqrt(1 - u * u);
  return out.set(s * Math.cos(t), u, s * Math.sin(t));
}

/** Request direction with a random cone jitter (spread 0..1); omni when no direction set. */
function dirJitter(q: EmitReq, spread: number, out: THREE.Vector3): THREE.Vector3 {
  const len = Math.hypot(q.dx, q.dy, q.dz);
  randomUnit(out);
  if (len < 1e-4) return out;
  out.multiplyScalar(spread);
  out.x += q.dx / len;
  out.y += q.dy / len;
  out.z += q.dz / len;
  return out.normalize();
}

/** Set main (end) and accent (start) colours on a request. */
export function tint(q: EmitReq, color: RGB, accent: RGB, alpha = 1): EmitReq {
  q.r = color[0];
  q.g = color[1];
  q.b = color[2];
  q.r2 = accent[0];
  q.g2 = accent[1];
  q.b2 = accent[2];
  q.alpha = alpha;
  return q;
}
